import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsExporting from 'highcharts/modules/exporting';
import highchartsExportData from 'highcharts/modules/export-data';
import noDataToDisplay from 'highcharts/modules/no-data-to-display';
import { locale, useAppContext } from '../../contexts/AppContext';
import { useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import { getYearlyDaysFromDate } from '../../utils/utils';
import { plotLines } from '../../models/Models';
import ChartHelpTooltip from '../common/ChartHelpTooltip';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

dayjs.extend(dayOfYear);

highchartsExporting(Highcharts);
highchartsExportData(Highcharts);
noDataToDisplay(Highcharts);

// show soiling_mass_rate only in int to allow Sebastian to validate the calculations
const linesToDisplay = ['local', 'int'].includes(import.meta.env.VITE_ENVIRONMENT)
  ? ['soiling_ratio', 'rain', 'soiling_mass_rate']
  : ['soiling_ratio', 'rain'];

type Props = {
  data: any;
  yearly_cleaning_days: any;
  rain_threshold: number;
  ymin: number;
  ymax: number;
};

const SoilingRatio = ({ data, yearly_cleaning_days, rain_threshold, ymin, ymax }: Props) => {
  const { isDarkMode } = useAppContext();
  const [chartOptions, setChartOptions] = useState(null);
  const chartComponent = useRef(null);
  const [copiedText, setCopiedText] = useState(false);

  useEffect(() => {
    if (data.hasOwnProperty('daily') && data.daily.hasOwnProperty('values') && data.daily.hasOwnProperty('times')) {
      prepareHighcharts(prepareData());
    }
  }, [data]);

  const handleTextCopy = (text: string) => {
    copyText(text);
    setCopiedText(true);
    setTimeout(() => {
      setCopiedText(false);
    }, 1500);
  };

  const copyText = (text) => {
    let txt = 'Month\tSoiling Ratio\tSoiling Level\n';
    text.forEach((t: any, index: number) => {
      let d = new Date(Date.parse(t));
      let ts = d.toLocaleString(locale, { month: 'long' });
      txt +=
        ts +
        '\t' +
        Math.round(data.monthly?.values?.soiling_ratio[index] * 10000) / 10000 +
        '\t' +
        Math.round((1 - data.monthly?.values?.soiling_ratio[index]) * 10000) / 10000 +
        '\n';
    });
    navigator.clipboard.writeText(txt);
  };

  const prepareData = () => {
    let timeseries_data = [];
    Object.keys(data.daily?.values)
      .filter((k) => linesToDisplay.includes(k))
      .forEach((key, index) => {
        timeseries_data.push({
          name: plotLines[key].name,
          type: key === 'rain' ? 'column' : 'line',
          color: plotLines[key].color,
          data: data.daily?.values[key],
          yAxis: index,
        });
      });
    return timeseries_data;
  };

  const prepareHighcharts = (highChartsData) => {
    const chart = {
      chart: {
        type: 'line',
        zoomType: 'x',
      },
      title: {
        text: 'Typical Soiling Year', // (' + Math.round(data?.average_soiling_ratio*1000)/1000 + ')',
        // align: 'left',
      },
      xAxis: {
        //type: 'datetime',
        categories: data.daily?.times,
        title: { text: 'Date' },
        labels: {
          // format: '{value:%e/%m}'
          formatter: function () {
            let d = new Date(Date.parse(this.value));
            return d.getDate() + '/' + ('0' + (d.getMonth() + 1)).slice(-2);
          },
        },
        gridLineWidth: 1,
        plotLines: getYearlyDaysFromDate(yearly_cleaning_days).map((val) => {
          let d = new Date(Date.parse(val));
          return {
            label: {
              text: 'cleaning',
            },
            color: 'green',
            value: dayjs(val).dayOfYear() - 1,
            width: 2,
          };
        }),
      },
      yAxis: Object.keys(data.daily?.values)
        .filter((k) => linesToDisplay.includes(k))
        .map((k) => {
          return {
            title: { text: plotLines[k].label }, // + (data.daily?.units[k] !== '1' ? ' (' + data.daily?.units[k] + ')' : '') },
            opposite: k !== 'soiling_ratio',
            plotLines:
              k === 'rain'
                ? [
                    {
                      label: { text: plotLines['rain_threshold'].label },
                      value: rain_threshold,
                      width: 2,
                      color: plotLines['rain_threshold'].color,
                    },
                  ]
                : [],
            // Set ymin and ymax for soiling ratio only
            ...(k == 'soiling_ratio' && {
              max: ymax,
              min: ymin,
              tickInterval: 0.2, // Tick marks every 0.2
              startOnTick: true, // Start on a tick mark
              endOnTick: true, // End on a tick mark
              allowDecimals: true,
            }),
          };
        }),
      // [{
      //   title: { text: 'Soiling Ratio' },
      //   gridLineWidth: 1
      // }],
      legend: {
        enabled: true,
      },
      tooltip: {
        valueDecimals: 4,
        shared: true,
        // formatter: function () {
        //   let result =
        //     `<span style='font-size: "smaller"'><b>` + this.series.name + `</b></span><br/>` +
        //     '<span style="color:' +
        //     this.point.color +
        //     '">\u25CF</span>Rain Score: <b>' +
        //     this.point.x.toFixed(2) +
        //     '</b><br/>' +
        //     '<span style="color:' +
        //     this.point.color +
        //     '">\u25CF</span>Deposition Score: <b>' +
        //     this.point.y.toFixed(2) +
        //     '</b><br/>';
        //   // TODO: Refactor because of deprecated window.event
        //   return result;
        // },
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        area: {
          stacking: 'normal',
          marker: {
            enabled: false,
          },
        },
      },
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
      },
      series: highChartsData,
    };
    setChartOptions(chart);
  };

  return (
    <>
      <Box sx={{ /*display: 'flex',*/ justifyContent: 'center', pl: 1, pr: 1 }}>
        <>
          <Box display="flex" alignItems="center">
            <Typography variant="h5" color="text.primary" sx={{ mt: 0 }}>
              Soiling Ratio
            </Typography>
            <ChartHelpTooltip chartType="soilingRatio" />
          </Box>
          <HighchartsReact
            ref={chartComponent}
            containerProps={{ className: isDarkMode ? 'dark-chart' : '', style: { width: '100%' } }}
            highcharts={Highcharts}
            options={chartOptions}
          />
          <Typography variant="h5" noWrap color="text.primary" sx={{ mt: 1 }}>
            Yearly Average Soiling Ratio
          </Typography>
          <Typography>Soiling Ratio: {Math.round(data.yearly?.values?.soiling_ratio[0] * 10000) / 100} %</Typography>
          <Typography>Soiling Level: {Math.round((1 - data.yearly?.values?.soiling_ratio[0]) * 10000) / 100} %</Typography>
          <Typography variant="h5" noWrap color="text.primary" sx={{ mt: 1 }}>
            Monthly Average Soiling Ratio
            <Tooltip title={copiedText ? 'Table copied!' : 'Copy table'}>
              <IconButton
                onClick={() => {
                  handleTextCopy(data.monthly?.times);
                }}
              >
                {copiedText ? <CheckCircleIcon fontSize="small" sx={{ color: '#008000' }} /> : <ContentCopyRoundedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table sx={{ minWidth: 650 }} size="small" aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Soiling Ratio (%)</TableCell>
                  <TableCell align="right">Soiling Level (%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.monthly?.times?.map((t: any, index: number) => {
                  let d = new Date(Date.parse(t));
                  let ts = d.toLocaleString(locale, { month: 'long' });
                  let soiling_ratio = Math.round(data.monthly?.values?.soiling_ratio[index] * 10000) / 100;
                  let soiling_level = Math.round((1 - data.monthly?.values?.soiling_ratio[index]) * 10000) / 100;
                  return (
                    <TableRow key={t} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        {ts}
                      </TableCell>
                      <TableCell align="right">{soiling_ratio}</TableCell>
                      <TableCell align="right">{soiling_level}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      </Box>
    </>
  );
};

export default SoilingRatio;
